import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, apiImageUrl } from '../../utils/streamApi';
import { getAlertPollMs } from '../../utils/alertPollConfig';
import { downloadAlertLogs } from '../../utils/alertLogExport';
import useSpeak from '../../utils/useSpeak';
import IndustrialLayout, { toast } from './IndustrialLayout';
import { FaceMatchEvidence } from '../../components/FaceMatchScore';

const PER_PAGE = 40;   // alerts fetched per page (paginated — avoids loading 300 thumbnails at once)
const EXPORT_CAP = 5000;   // ceiling on a filter-wide export / bulk review (one /info call per alert)

// Review-status filter — the same verdict taxonomy the Alerts screen labels with, so
// a slice ("everything an operator marked false") is exportable as training data.
const REVIEW_FILTERS = [
  { value: 'unlabelled',          label: 'Unreviewed' },
  // "Labelled" = reviewed, any verdict — the slice you want when pulling labelled
  // data out for tuning or retraining, where the verdict is a column in the export
  // rather than the thing you select on.
  { value: 'labelled',            label: '★ Labelled (any)' },
  { value: 'correct',             label: '✓ Real' },
  // One "false" bucket on purpose. Wrong-object alerts land here too, separated by
  // their reason rather than by a second verdict, so an export never has to union
  // two filters to mean "false".
  { value: 'false_alert',         label: '✗ False' },
];

// Incident STATUS — a different axis from the review verdict. Review answers "was
// the AI right?" (feeds tuning and the export); status answers "is anyone still on
// the hook for this?". A correct alert can be ignored (real person — the gardener),
// and a false alert can still need a resolution note (the siren fired, neighbours
// called). Both Resolved and Ignored are terminal; Reopen clears them.
const STATUS_FILTERS = [
  { value: 'open',     label: '◻ Open' },
  { value: 'resolved', label: '✓ Resolved' },
  { value: 'ignored',  label: '⊘ Ignored' },
];
const STATUS_LABEL = { resolved: '✓ Resolved', ignored: '⊘ Ignored' };

// ── Classify by the alert's FILENAME PREFIX (unknown_/banned_/fire_…), which is
// the real backend type. The alert object carries no label field. ──
function typeOf(a) { return String(a?.filename || '').split('_')[0].toLowerCase(); }

// filename prefix → module key (used for labels, counts, contextual actions)
const TYPE_MODULE = {
  unknown: 'unknown', banned: 'banned', known: 'known',
  fire: 'fire', smoke: 'fire', helmet: 'ppe', ppe: 'ppe',
  crowd: 'crowd', arms: 'weapon', weapon: 'weapon', gun: 'weapon',
  camera: 'tamper', tamper: 'tamper', instant: 'person',
  after: 'afterhours', afterhours: 'afterhours', unattended: 'unattended',
  zone: 'intrusion', boundary: 'intrusion',
  restricted: 'restricted',   // known person seen OUTSIDE their allowed hours
  fall: 'fall',               // person collapsed and stayed down
  face: 'face_cover',         // face_cover_… — front face, nose/mouth covered
  camoffline: 'camera_offline', // synthetic, no snapshot — see is_camera_offline
};
function typeToModule(t) { return TYPE_MODULE[t] || t || 'other'; }
function moduleOf(a) { return typeToModule(typeOf(a)); }

// Human label for an alert. Prefer the logged label; otherwise fall back to the
// module name derived from the filename — never a bare "Event", so a filtered
// list always shows what type each row is.
function displayLabel(a) {
  const raw = String(a?.label || '').replace(/[^\w\s:.\-/]/g, '').trim();
  return raw || MODULE_LABELS[moduleOf(a)] || 'Event';
}

// Epoch ms for sorting, with unparseable stamps sinking to the bottom rather
// than poisoning the comparison with NaN.
function tsNum(a) {
  const n = parseAlertTs(a?.ts || a?.timestamp);
  return Number.isNaN(n) ? 0 : n;
}

// Rough priority categorisation of the AI modules.
const SEV_BY_MODULE = {
  // Critical — safety / security emergencies. A fall sits here on purpose: it is a
  // medical event, and the cost of burying it under "high" is somebody on the floor.
  banned: 'critical', fire: 'critical', weapon: 'critical', tamper: 'critical', intrusion: 'critical',
  fall: 'critical', camera_offline: 'critical',
  // High — needs a look; notify-only by default (a mask is not a threat by itself)
  face_cover: 'high',
  // High — security / compliance events needing a look
  unknown: 'high', ppe: 'high', crowd: 'high', afterhours: 'high', unattended: 'high', restricted: 'high',
  // Info — informational / expected
  known: 'info', person: 'info', other: 'info',
};
function severityOf(a) { return SEV_BY_MODULE[moduleOf(a)] || 'info'; }

const MODULE_LABELS = {
  unknown: 'Access', banned: 'Watchlist', known: 'Known', fire: 'Fire', ppe: 'PPE',
  tamper: 'Tamper', crowd: 'Crowd', weapon: 'Weapon', afterhours: 'After-hours',
  unattended: 'Unattended', person: 'Person', intrusion: 'Restricted Zone', other: 'Other',
  restricted: 'Restricted (known)', fall: 'Person down', camera_offline: 'Camera Offline',
  face_cover: 'Face covered',
};

// Reverse maps for SERVER-SIDE filtering (module/severity → filename type prefixes).
const MODULE_TYPES = {};  // module key → [type prefixes]
const SEV_TYPES    = { critical: [], high: [], info: [] };
Object.entries(TYPE_MODULE).forEach(([t, m]) => {
  (MODULE_TYPES[m] = MODULE_TYPES[m] || []).push(t);
  SEV_TYPES[SEV_BY_MODULE[m] || 'info'].push(t);
});

// YYYY-MM-DD in LOCAL time. toISOString() would be UTC: in IST (+5:30) that
// makes "today" read as yesterday between 00:00 and 05:30 — the exact window
// most night alerts fire in, so the default range opened on the wrong day.
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// n days back from today, as YYYY-MM-DD.
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return isoDay(d); };

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Compact label for the mobile date button: "Today", "28 Oct", "22 – 28 Oct".
function rangeLabel(from, to) {
  const short = s => { const [, m, d] = s.split('-'); return `${+d} ${MON[+m - 1]}`; };
  if (from !== to) return `${short(from)} – ${short(to)}`;
  return from === isoDay(new Date()) ? 'Today' : from === daysAgo(1) ? 'Yesterday' : short(from);
}

// REVIEW verdicts per module — "was this alert real?". Every id maps to a REAL
// backend call (see doAction); the wording is module-specific but they all land on
// /alerts/{id}/verdict, the same taxonomy the Alerts screen and tuning read from.
const PERSON_MODULES = new Set(['banned', 'unknown', 'intruder']);
function reviewActions(mod) {
  const acts = [{ id: 'confirm', label: '✓ Real', cls: 'ok' }];
  if (mod === 'banned')        acts.push({ id: 'not_match', label: '✗ Not a match', cls: '' });
  else if (mod === 'fire')     acts.push({ id: 'dismiss',   label: '✗ Not fire/smoke', cls: '' });
  else if (mod === 'ppe')      acts.push({ id: 'dismiss',   label: '✗ Not a violation', cls: '' });
  else if (mod === 'intruder') acts.push({ id: 'dismiss',   label: '✗ Not an intruder', cls: '' });
  else if (mod === 'fall')     acts.push({ id: 'dismiss',   label: '✗ Not a fall', cls: '' });
  else if (mod === 'face_cover') {
    // Its own feedback profile: a face-cover false alert must carry a face-cover
    // reason (the box refuses the person-detection ones). "Allowed here" feeds
    // zone / quiet-hour suggestions, never a less sensitive detector.
    acts[0].label = '✓ Face was covered';
    acts.push({ id: 'fc_vis',     label: '✗ Face was visible', cls: '' });
    acts.push({ id: 'fc_allowed', label: '✗ Allowed here',     cls: '' });
    acts.push({ id: 'fc_back',    label: '✗ Back of head',     cls: 'warn' });
    return acts;
  }
  else                         acts.push({ id: 'dismiss',   label: '✗ False alert', cls: '' });
  // "Wrong object, but a person WAS there" — must never count as a false alarm
  // against a confidence bar, so it gets its own verdict rather than ✗.
  if (PERSON_MODULES.has(mod)) acts.push({ id: 'wrong_obj', label: '⚠ Wrong object', cls: 'warn' });
  return acts;
}

// ACTIONS = what the operator DOES about it (ownership + enrolment), as opposed to
// the review verdict above. There is no "Mark authorised" button: it called
// addToKnown, which is exactly what the "Add to Known" button below does, and it
// rendered even with no face crop to enrol. Enrolment is gated on active.has_face
// instead. There is no "Resolve" either — ownership is Acknowledge, and closure is
// the Review verdict (alert_feedback.mark_verdict sets closed on 'correct').

// Parse an alert timestamp into epoch ms. Handles epoch numbers, ISO strings,
// and the backend's compact "YYYYMMDD_HHMMSS" (or "YYYYMMDD") filename stamp.
function parseAlertTs(ts) {
  if (ts == null || ts === '') return NaN;
  if (typeof ts === 'number') return ts * (ts < 1e12 ? 1000 : 1);
  const s = String(ts);
  const m = s.match(/^(\d{4})(\d{2})(\d{2})(?:_(\d{2})(\d{2})(\d{2}))?$/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0)).getTime();
  const p = Date.parse(s);
  return Number.isNaN(p) ? NaN : p;
}

// Human-readable absolute time, or '—' when unparseable.
function fmtAlertTs(ts) {
  const d = parseAlertTs(ts);
  return Number.isNaN(d) ? '—' : new Date(d).toLocaleString();
}

function agoLabel(ts) {
  const d = parseAlertTs(ts);
  if (Number.isNaN(d)) return '';
  const secs = Math.max(0, (Date.now() - d) / 1000);
  if (secs < 60) return 'NOW';
  if (secs < 3600) return `${Math.floor(secs / 60)} MIN`;
  if (secs < 86400) return `${Math.floor(secs / 3600)} HR`;
  return `${Math.floor(secs / 86400)} D`;
}

function ymd(date) {
  return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
}

// Small multi-select combo for narrowing a long "cameras offline" list — plain
// checkboxes in a dropdown, applied immediately (this only filters what's
// already loaded, no API call to stage/debounce, unlike ManageRecordingsPage's
// CamMultiSelect which exists to batch expensive stream lookups).
function CamOfflineFilterCombo({ cameras, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = (idx) => {
    onChange(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const label = selected.size === 0 ? 'All cameras'
    : selected.size === cameras.length ? `All cameras (${selected.size})`
    : selected.size === 1 ? (cameras.find(c => selected.has(c.cam_idx))?.cam_name || '1 camera')
    : `${selected.size} cameras`;

  const boxStyle = { padding: '7px 10px', borderRadius: 8, fontSize: 12.5,
    border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)' };

  return (
    <div style={{ position: 'relative', marginBottom: 10 }} ref={ref}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ ...boxStyle, width: '100%', boxSizing: 'border-box', display: 'flex',
                 justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
        <span>{label}</span><span>▾</span>
      </button>
      {open && (
        <div style={{ ...boxStyle, position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                      marginTop: 4, maxHeight: 220, overflowY: 'auto', padding: 6 }}>
          <div style={{ display: 'flex', gap: 12, padding: '2px 4px 6px', borderBottom: '1px solid var(--ind-line)', marginBottom: 4 }}>
            <button type="button" onClick={() => onChange(new Set())}
              style={{ fontSize: 11, background: 'none', border: 'none', color: 'var(--ind-tx)', cursor: 'pointer', opacity: 0.8, padding: 0 }}>
              Clear
            </button>
            <button type="button" onClick={() => onChange(new Set(cameras.map(c => c.cam_idx)))}
              style={{ fontSize: 11, background: 'none', border: 'none', color: 'var(--ind-tx)', cursor: 'pointer', opacity: 0.8, padding: 0 }}>
              Select all
            </button>
          </div>
          {cameras.map(c => (
            <label key={c.cam_idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 4px', fontSize: 12.5, cursor: 'pointer' }}>
              <input type="checkbox" checked={selected.has(c.cam_idx)} onChange={() => toggle(c.cam_idx)} />
              {c.cam_name}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function AlertImage({ filename, className }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let live = true;
    apiImageUrl(`/alerts/${encodeURIComponent(filename)}/image`).then(u => live && setSrc(u));
    return () => { live = false; };
  }, [filename]);
  if (err || !src) return <div className="ph">📷</div>;
  return <img className={className} src={src} alt="" onError={() => setErr(true)} />;
}

function AlertVideo({ filename }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let live = true;
    apiImageUrl(`/alerts/${encodeURIComponent(filename)}/video`).then(u => live && setSrc(u));
    return () => { live = false; };
  }, [filename]);
  if (!src) return <div className="ph" style={{ color: '#64748B' }}>loading clip…</div>;
  // No autoplay → browsers don't force-mute, so the sound control works on play.
  // (Alert clips are silent unless per-camera audio recording is enabled.)
  return <video src={src} controls playsInline style={{ width: '100%', display: 'block' }} />;
}

export default function IndustrialOpsPage() {
  const navigate = useNavigate();
  const [alerts, setAlerts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [sel, setSel]         = useState(null);
  const [sevFilter, setSev]   = useState(null);
  const [modFilter, setMod]   = useState(null);   // filter by AI module
  const [asgFilter, setAsg]   = useState('');     // filter by assignee ('' any, '__none__' unassigned, or user id)
  const [showVideo, setVid]   = useState(false);  // clip toggle in the detail pane
  const [lightbox, setLightbox] = useState(null); // filename of the enlarged image, or null
  const [enroll, setEnroll]   = useState(null);   // { filename, mode:'known'|'banned' } → face enrollment popup
  const [busy, setBusy]       = useState(false);
  const [refreshing, setRefreshing] = useState(false); // manual refresh spinner
  const [users, setUsers]     = useState([]);          // for the escalate dropdown
  const [assignments, setAssignments] = useState({});  // filename → ownership record
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [summary, setSummary] = useState({ by_type: {}, total: 0 });   // backend-wide counts for the date range
  const [camFilter, setCam]   = useState('');     // filter by camera name ('' = all)
  const [camOffSelected, setCamOffSelected] = useState(() => new Set());  // multi-select filter inside the "Cameras offline" detail list (empty = show all)
  const [vdtFilter, setVdt]   = useState('');     // filter by review verdict ('' = all)
  const [resFilter, setRes]   = useState('');     // filter by incident status ('' = all)
  const [bellFilter, setBell] = useState(false);  // only the alerts that rang the doorbell (PRVSK-139)
  const [resolveOn, setResolveOn] = useState(null); // { status, names, scope } → comment popup
  const [filtersOpen, setFiltersOpen] = useState(false);  // filter stack starts collapsed
  const [datesOpen, setDatesOpen] = useState(false);      // mobile: date-range sheet
  const [dlBusy, setDlBusy]   = useState(false);  // export in flight
  const [sirenOn, setSirenOn] = useState(false);  // physical-response toggles (optimistic)
  const [lightOn, setLightOn] = useState(false);
  // Speak — same record/send + type-to-speak path as the mobile dashboard.
  const { speakText, setSpeakText, speaking, recording, micError,
          toggleRecord, speakTyped } = useSpeak();
  const [checked, setChecked] = useState(() => new Set());  // multi-select for bulk actions
  const [from, setFrom] = useState(() => isoDay(new Date()));  // default: today only
  const [to, setTo]     = useState(() => isoDay(new Date()));
  const pageRef     = useRef(0);
  const fetchingRef  = useRef(false);
  const loadSeqRef   = useRef(0);   // supersedes stale in-flight loads on filter/date change
  const triageRef   = useRef(null);
  const queryRef    = useRef('');   // date + `only` query suffix (kept in a ref so callbacks stay stable)
  // Doorbell filter rides on the LIST fetch only, never on queryRef: queryRef also
  // scopes "delete all matching", and DELETE /ui/alerts has no doorbell filter — so
  // the bell must stay a view refinement, like camera and assignee, or "delete all"
  // would quietly take every alert in the range.
  const bellRef     = useRef(false);

  // Server-side type filter (only=…) derived from the active module / severity pill.
  const onlyParam = () => {
    if (modFilter) return (MODULE_TYPES[modFilter] || [modFilter]).join(',');
    if (sevFilter) return (SEV_TYPES[sevFilter] || []).join(',');
    return '';
  };

  const fetchPage = useCallback(async (page) => {
    const r = await apiFetch(`/ui/alerts?page=${page}&per_page=${PER_PAGE}&exclude=snapshot${queryRef.current}${bellRef.current ? '&doorbell=true' : ''}`);
    const d = await r.json();
    return (d.alerts || []).map(a => ({ ...a, sev: severityOf(a) }));
  }, []);

  const loadSummary = useCallback(async (df, dt, vdt = '', res = '') => {
    try {
      const d = await apiFetch(`/ui/alerts/summary?exclude=snapshot&date_from=${df}&date_to=${dt}${vdt ? `&verdict=${vdt}` : ''}${res ? `&resolution=${res}` : ''}`).then(r => r.json());
      setSummary(d && d.by_type ? d : { by_type: {}, total: 0 });
    } catch { /* ignore */ }
  }, []);

  // Reset to page 0 (spinner) or silently refresh page 0 (prepend new alerts).
  // A non-silent load (filter/date change) must ALWAYS run — even mid-fetch — else
  // changing the date while an auto-refresh or "load more" is in flight is silently
  // dropped and the list never updates. A seq token makes the latest load win and
  // ignores stale in-flight responses.
  const load = useCallback(async (silent = false) => {
    if (silent && fetchingRef.current) return;   // don't stack silent auto-refreshes
    const seq = ++loadSeqRef.current;
    fetchingRef.current = true;
    if (!silent) { setLoading(true); setError(null); }
    try {
      const items = await fetchPage(0);
      if (seq !== loadSeqRef.current) return;     // superseded by a newer load → drop
      if (silent) {
        setAlerts(prev => {
          const fresh = items.filter(a => !prev.some(p => p.filename === a.filename));
          if (fresh.length) toast(`${fresh.length} new alert${fresh.length === 1 ? '' : 's'}`, fresh.some(a => a.sev === 'critical') ? 'err' : 'warn');
          // prepend new, keep everything already paged-in below
          return fresh.length ? [...fresh, ...prev] : prev;
        });
      } else {
        pageRef.current = 0;
        setHasMore(items.length === PER_PAGE);
        setAlerts(items);
        setSel(s => (s && items.some(a => a.filename === s)) ? s : (items[0]?.filename || null));
      }
    } catch {
      if (!silent && seq === loadSeqRef.current) setError('Failed to load alerts');
    } finally {
      if (seq === loadSeqRef.current) { fetchingRef.current = false; if (!silent) setLoading(false); }
    }
  }, [fetchPage]);

  const loadMore = useCallback(async () => {
    if (fetchingRef.current || !hasMore) return;
    fetchingRef.current = true;
    setLoadingMore(true);
    try {
      const next = pageRef.current + 1;
      const items = await fetchPage(next);
      pageRef.current = next;
      setHasMore(items.length === PER_PAGE);
      setAlerts(prev => {
        const seen = new Set(prev.map(p => p.filename));
        return [...prev, ...items.filter(a => !seen.has(a.filename))];
      });
    } catch { /* ignore */ }
    finally { setLoadingMore(false); fetchingRef.current = false; }
  }, [hasMore]);

  const loadAssignments = useCallback(async () => {
    try {
      const d = await apiFetch('/ui/alert-assignments').then(r => r.json());
      setAssignments(d.assignments || {});
    } catch { /* ignore */ }
  }, []);

  // Reload + summary whenever the date range or module/severity/review filter changes.
  useEffect(() => {
    const df = from.replace(/-/g, ''), dt = to.replace(/-/g, '');
    const only = onlyParam();
    queryRef.current = `&date_from=${df}&date_to=${dt}${only ? `&only=${only}` : ''}${vdtFilter ? `&verdict=${vdtFilter}` : ''}${resFilter ? `&resolution=${resFilter}` : ''}`;
    bellRef.current = bellFilter;
    load();
    loadSummary(df, dt, vdtFilter, resFilter);
  }, [from, to, sevFilter, modFilter, vdtFilter, resFilter, bellFilter]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link from the navbar critical badge: /industrial?sev=critical pre-selects
  // severity. The param is then STRIPPED from the URL — left in place it re-applied
  // on every reload and back-navigation, so the console looked permanently filtered
  // to critical and the newest alerts never showed.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const p = sp.get('sev');
    if (!p || !['critical', 'high', 'info'].includes(p)) return;
    setSev(p);
    sp.delete('sev');
    navigate({ search: sp.toString() }, { replace: true });
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadAssignments(); }, [loadAssignments]);
  useEffect(() => { setVid(false); setCamOffSelected(new Set()); }, [sel]);   // reset clip view / camera filter when switching alert

  // Assignable operators (web users) for the escalate dropdown.
  useEffect(() => {
    apiFetch('/ui/assignees').then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.users) setUsers(d.users); }).catch(() => {});
  }, []);

  // Auto-refresh on the SAME cadence as the Alerts poll setting (0 = disabled).
  useEffect(() => {
    const ms = getAlertPollMs();
    if (!ms) return;
    const t = setInterval(() => { load(true); loadAssignments(); }, ms);
    return () => clearInterval(t);
  }, [load, loadAssignments]);

  // Manual "refresh now" — pulls new alerts, assignments and summary for the current range.
  const refreshNow = useCallback(async () => {
    setRefreshing(true);
    const df = from.replace(/-/g, ''), dt = to.replace(/-/g, '');
    try { await Promise.all([load(true), loadAssignments(), loadSummary(df, dt, vdtFilter, resFilter)]); }
    finally { setRefreshing(false); }
  }, [from, to, vdtFilter, resFilter, load, loadAssignments, loadSummary]);

  // Severity + module counts from the backend summary (whole date range, not just loaded).
  const counts = useMemo(() => {
    const c = { critical: 0, high: 0, info: 0 };
    Object.entries(summary.by_type || {}).forEach(([t, n]) => { c[SEV_BY_MODULE[typeToModule(t)] || 'info'] += n; });
    return c;
  }, [summary]);
  const moduleCounts = useMemo(() => {
    const m = {};
    Object.entries(summary.by_type || {}).forEach(([t, n]) => { const k = typeToModule(t); m[k] = (m[k] || 0) + n; });
    return m;
  }, [summary]);

  const assigneeOf = useCallback((fn) => {
    const r = assignments[fn];
    return r?.acknowledged_by || r?.escalated_to || null;
  }, [assignments]);

  // Assignee counts (whole assignments map) for the assignee tiles.
  const assigneeCounts = useMemo(() => {
    const m = {};
    Object.values(assignments).forEach(r => {
      const who = r?.acknowledged_by || r?.escalated_to;
      if (who) m[String(who)] = (m[String(who)] || 0) + 1;
    });
    return m;
  }, [assignments]);

  // Distinct cameras present in the loaded alerts — for the camera filter pills.
  const cameraList = useMemo(() => {
    const s = new Set();
    alerts.forEach(a => { if (a.cam_name) s.add(a.cam_name); });
    return [...s].sort();
  }, [alerts]);

  // Rows: module/severity already filtered server-side; assignee + camera client-side.
  const rows = useMemo(() => {
    let list = alerts;
    if (asgFilter === '__none__') list = list.filter(a => !assigneeOf(a.filename));
    else if (asgFilter)           list = list.filter(a => String(assigneeOf(a.filename)) === asgFilter);
    if (camFilter)                list = list.filter(a => a.cam_name === camFilter);
    // NEWEST FIRST. Sorting by severity floated every critical to the top whatever
    // its age, which read as "the board is filtered to critical" and buried the
    // alert that just fired. Severity is on the tag and in the severity filter.
    return [...list].sort((a, b) => tsNum(b) - tsNum(a));
  }, [alerts, asgFilter, camFilter, assigneeOf]);

  const active = alerts.find(a => a.filename === sel) || rows[0];
  const checkedShown   = rows.reduce((n, r) => n + (checked.has(r.filename) ? 1 : 0), 0);
  const allShownChecked = rows.length > 0 && checkedShown === rows.length;
  const toggleAllShown = () => setChecked(prev => {
    const s = new Set(prev);
    if (rows.every(r => s.has(r.filename))) rows.forEach(r => s.delete(r.filename));
    else rows.forEach(r => s.add(r.filename));
    return s;
  });
  const userName = (id) => (users.find(u => String(u.id) === String(id))?.username) || (id != null ? String(id) : '—');

  const isMobile = useIsMobile();
  const asn = active ? assignments[active.filename] : null;

  // ── Actions ── all persist to the backend: verdict → alert log;
  // acknowledge / escalate → alert_assignments.json (shared incident ownership).
  const setVerdict = async (a, v, reason = null) => {   // v: 'correct' | 'false_alert'
    if (!a) return;
    setBusy(true);
    try {
      const r = await apiFetch(`/alerts/${encodeURIComponent(a.filename)}/verdict`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verdict: v, false_reason: reason }),
      });
      if (!r.ok) throw new Error();
      setAlerts(list => list.map(x => x.filename === a.filename
        ? { ...x, verdict: v, false_reason: reason } : x));
      toast(v === 'correct' ? 'Marked as a real event.' : 'Marked false — sample sent for tuning.', v === 'correct' ? 'ok' : 'warn');
    } catch {
      toast('Failed to save verdict.', 'err');
    } finally { setBusy(false); }
  };

  // Open the enrollment popup (existing-person picker + new name, Known/Banned).
  const addToBanned = (a) => { if (a) setEnroll({ filename: a.filename, mode: 'banned' }); };
  const addToKnown  = (a) => { if (a) setEnroll({ filename: a.filename, mode: 'known'  }); };

  // Device response — sound + light as toggles over the existing hardware endpoints.
  // State is optimistic (same as the dashboard's siren switch): the relay endpoints
  // are fire-and-forget, so the button reverts if the call fails rather than
  // claiming a state the hardware never reached.
  const DEVICE_PATHS = {
    siren: { on: '/controls/siren',    off: '/controls/stop',       label: 'Warning sound' },
    light: { on: '/controls/light/on', off: '/controls/light/off',  label: 'Light' },
  };
  const toggleDevice = async (key, on) => {
    const d = DEVICE_PATHS[key];
    const setState = key === 'siren' ? setSirenOn : setLightOn;
    setState(on);
    setBusy(true);
    try {
      const r = await apiFetch(on ? d.on : d.off, { method: 'POST' });
      if (!r.ok) throw new Error();
      toast(`${d.label} ${on ? 'on' : 'off'}.`, on ? 'warn' : 'ok');
    } catch {
      setState(!on);   // hardware never got it — don't leave the button lying
      toast('Device command failed.', 'err');
    } finally { setBusy(false); }
  };

  const sayText = async () => {
    if (!speakText.trim()) return;
    const ok = await speakTyped();
    if (ok) { setSpeakText(''); toast('Spoken on site speaker.', 'ok'); }
    else toast('Speak failed.', 'err');
  };

  // Download the active alert's snapshot or clip. apiFetch injects the auth header,
  // so we pull the asset as a blob and save it (a plain <a download> can't carry the token).
  const downloadAsset = async (a, kind) => {   // kind: 'image' | 'video'
    if (!a) return;
    const ext = kind === 'video' ? 'mp4' : 'jpg';
    setBusy(true);
    try {
      const r = await apiFetch(`/alerts/${encodeURIComponent(a.filename)}/${kind}`);
      if (!r.ok) throw new Error();
      const url = URL.createObjectURL(await r.blob());
      const el  = document.createElement('a');
      el.href = url; el.download = `${a.filename}.${ext}`;
      document.body.appendChild(el); el.click();
      setTimeout(() => { URL.revokeObjectURL(url); document.body.removeChild(el); }, 1000);
      toast(`Downloaded ${kind === 'video' ? 'clip' : 'photo'}.`, 'ok');
    } catch {
      toast(`Failed to download ${kind === 'video' ? 'clip' : 'photo'}.`, 'err');
    } finally { setBusy(false); }
  };

  // Active filters as removable chips — rendered in the pinned head whether or not
  // the filter panel is open, so a filtered board is never mistaken for an empty one.
  const activeFilters = [
    sevFilter && { key: 'sev', label: sevFilter.toUpperCase(),                       clear: () => setSev(null) },
    modFilter && { key: 'mod', label: MODULE_LABELS[modFilter] || modFilter,         clear: () => setMod(null) },
    vdtFilter && { key: 'vdt', label: REVIEW_FILTERS.find(v => v.value === vdtFilter)?.label || vdtFilter, clear: () => setVdt('') },
    resFilter && { key: 'res', label: STATUS_FILTERS.find(s => s.value === resFilter)?.label || resFilter,  clear: () => setRes('') },
    camFilter && { key: 'cam', label: `🎥 ${camFilter}`,                             clear: () => setCam('') },
    bellFilter && { key: 'bell', label: '🔔 Doorbell',                                 clear: () => setBell(false) },
    asgFilter && { key: 'asg', label: asgFilter === '__none__' ? 'Unassigned' : `👤 ${userName(asgFilter)}`, clear: () => setAsg('') },
  ].filter(Boolean);

  // Human-readable description of the active filter, spelled out in every
  // filter-wide confirm so a bulk action's scope is never a guess.
  const scopeLabel = () => [
    `${from} → ${to}`,
    modFilter ? MODULE_LABELS[modFilter] || modFilter : sevFilter || null,
    camFilter ? `🎥 ${camFilter}` : null,
    vdtFilter ? (REVIEW_FILTERS.find(v => v.value === vdtFilter)?.label || vdtFilter) : null,
    resFilter ? (STATUS_FILTERS.find(s => s.value === resFilter)?.label || resFilter) : null,
  ].filter(Boolean).join(' · ');

  // Delete EVERY alert matching the current date + module/severity filter in one
  // server-side call (not just the loaded page). Camera/assignee are view-only
  // refinements and are intentionally excluded, so the confirm spells out the scope.
  const deleteFiltered = async () => {
    // With the doorbell filter on, the server-side delete would ignore it and take
    // every alert in the range. Delete exactly the alerts in view instead, by name —
    // the same set Resolve all / Ignore all act on.
    if (bellFilter) {
      let names;
      try { names = await filteredFilenames(); } catch { toast('Could not list the alerts to delete.', 'err'); return; }
      if (!names.length) { toast('No alerts match the current filter.', 'warn'); return; }
      const scope = [`${from} → ${to}`, modFilter || sevFilter || null, '🔔 Doorbell only',
        camFilter ? `🎥 ${camFilter}` : null].filter(Boolean).join(' · ');
      if (!window.confirm(`Delete ${names.length} doorbell alert${names.length === 1 ? '' : 's'} matching:\n\n${scope}\n\nThis permanently removes their snapshots and clips. This cannot be undone.`)) return;
      setBusy(true);
      try {
        for (let i = 0; i < names.length; i += 8) {   // same chunked fan-out as deleteChecked
          await Promise.all(names.slice(i, i + 8).map(fn =>
            apiFetch(`/alerts/${encodeURIComponent(fn)}`, { method: 'DELETE' }).catch(() => {})));
        }
        toast(`Deleted ${names.length} alert${names.length === 1 ? '' : 's'}.`, 'ok');
        setSel(null);
        load(false);
        loadSummary(from.replace(/-/g, ''), to.replace(/-/g, ''), vdtFilter, resFilter);
      } catch { toast('Failed to delete alerts.', 'err'); }
      finally { setBusy(false); }
      return;
    }
    const q = `exclude=snapshot${queryRef.current}`;
    let n = summary.total;
    try {   // exact count for the active filter (includes module/severity via `only`)
      const c = await apiFetch(`/ui/alerts?page=0&per_page=1&${q}`).then(r => r.json());
      if (typeof c.total === 'number') n = c.total;
    } catch { /* fall back to summary.total */ }
    if (!n) { toast('No alerts match the current filter.', 'warn'); return; }
    const scope = `${from} → ${to}${modFilter ? ` · ${modFilter}` : sevFilter ? ` · ${sevFilter}` : ''}`;
    if (!window.confirm(`Delete ALL ${n} alert${n === 1 ? '' : 's'} matching:\n\n${scope}\n\nThis permanently removes their snapshots and clips. This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await apiFetch(`/ui/alerts?${q}`, { method: 'DELETE' });
      const d = await r.json();
      if (!r.ok) throw new Error();
      toast(`Deleted ${d.deleted ?? n} alert${(d.deleted ?? n) === 1 ? '' : 's'}.`, 'ok');
      setSel(null);
      load(false);
      loadSummary(from.replace(/-/g, ''), to.replace(/-/g, ''), vdtFilter, resFilter);
    } catch { toast('Failed to delete alerts.', 'err'); }
    finally { setBusy(false); }
  };

  // Bulk-delete the checkbox-selected alerts (only those currently shown).
  const deleteChecked = async () => {
    const names = rows.filter(r => checked.has(r.filename)).map(r => r.filename);
    if (!names.length) return;
    if (!window.confirm(`Delete ${names.length} selected alert${names.length === 1 ? '' : 's'}?\n\nThis permanently removes their snapshots and clips.`)) return;
    setBusy(true);
    try {
      for (let i = 0; i < names.length; i += 8) {   // chunk the fan-out — don't open hundreds of sockets at once
        await Promise.all(names.slice(i, i + 8).map(fn =>
          apiFetch(`/alerts/${encodeURIComponent(fn)}`, { method: 'DELETE' }).catch(() => {})));
      }
      const gone = new Set(names);
      setAlerts(list => {
        const next = list.filter(x => !gone.has(x.filename));
        setSel(s => (gone.has(s) ? (next[0]?.filename || null) : s));
        return next;
      });
      setChecked(new Set());
      toast(`Deleted ${names.length} alert${names.length === 1 ? '' : 's'}.`, 'ok');
      loadSummary(from.replace(/-/g, ''), to.replace(/-/g, ''), vdtFilter, resFilter);
    } catch { toast('Failed to delete selected alerts.', 'err'); }
    finally { setBusy(false); }
  };

  // ── Filter-wide operations ──
  // Every filename matching the current server-side filter (date + module/severity
  // + review status), then the client-side camera/assignee refinements applied on
  // top so a bulk action covers exactly the set the operator is looking at — not
  // just the page that happens to be scrolled in.
  const filteredFilenames = useCallback(async () => {
    const q = `exclude=snapshot${queryRef.current}`;
    const head = await apiFetch(`/ui/alerts?page=0&per_page=1&${q}`).then(r => r.json());
    const n = Math.min(EXPORT_CAP, Number(head.total) || 0);
    if (!n) return [];
    const d = await apiFetch(`/ui/alerts?page=0&per_page=${n}&${q}`).then(r => r.json());
    let list = d.alerts || [];
    if (camFilter)                list = list.filter(a => a.cam_name === camFilter);
    if (bellFilter)               list = list.filter(a => a.doorbell);
    if (asgFilter === '__none__') list = list.filter(a => !assigneeOf(a.filename));
    else if (asgFilter)           list = list.filter(a => String(assigneeOf(a.filename)) === asgFilter);
    return list.map(a => a.filename);
  }, [camFilter, bellFilter, asgFilter, assigneeOf]);

  // Bulk review — one /alerts/bulk-verdict call, same endpoint the Alerts screen's
  // bulk bar uses. `names` is either the checkbox selection or the whole filter.
  const bulkVerdict = async (verdict, names, scopeLabel) => {
    if (!names.length) return;
    if (!window.confirm(`Mark ${names.length} alert${names.length === 1 ? '' : 's'} as ${verdict === 'correct' ? 'REAL' : 'FALSE'}?\n\n${scopeLabel}\n\nThis writes a verdict on each — it is what threshold tuning and retraining read.`)) return;
    setBusy(true);
    try {
      const ids = names.map(n => n.replace(/\.(jpg|jpeg|png)$/i, ''));
      const r = await apiFetch('/alerts/bulk-verdict', { method: 'POST', json: { ids, verdict } });
      if (!r.ok) throw new Error();
      const d = await r.json();
      const done = new Set(names);
      setAlerts(list => list.map(a => (done.has(a.filename) ? { ...a, verdict } : a)));
      setChecked(new Set());
      toast(`Marked ${d.updated ?? names.length} as ${verdict === 'correct' ? 'real' : 'false'}.`, verdict === 'correct' ? 'ok' : 'warn');
      // A review-status filter is showing a set these alerts just left — refetch.
      if (vdtFilter) load(false);
      loadSummary(from.replace(/-/g, ''), to.replace(/-/g, ''), vdtFilter, resFilter);
    } catch { toast('Bulk review failed.', 'err'); }
    finally { setBusy(false); }
  };

  const bulkVerdictFiltered = async (verdict) => {
    setBusy(true);
    let names = [];
    try { names = await filteredFilenames(); }
    catch { toast('Could not list the filtered alerts.', 'err'); setBusy(false); return; }
    setBusy(false);
    if (!names.length) { toast('No alerts match the current filter.', 'warn'); return; }
    await bulkVerdict(verdict, names, scopeLabel());
  };

  // Export the full alert-log record (telemetry + config_snapshot + verdict) for a
  // set of alerts as one JSON file — the labelled-data pull for offline analysis
  // and retraining. Reuses the Alerts screen's log-export helper.
  const exportLogs = async (names, tag) => {
    if (!names.length) { toast('Nothing to export.', 'warn'); return; }
    setDlBusy(true);
    toast(`Fetching ${names.length} log${names.length === 1 ? '' : 's'}…`);
    try {
      const n = await downloadAlertLogs(names, {
        name: `ops_${tag}_${from.replace(/-/g, '')}-${to.replace(/-/g, '')}_${names.length}.json`,
      });
      toast(n ? `Exported ${n} log${n === 1 ? '' : 's'}.` : 'No log records found for these alerts.', n ? 'ok' : 'warn');
    } catch { toast('Export failed.', 'err'); }
    finally { setDlBusy(false); }
  };

  // ── Incident status (resolve / ignore / reopen) ──
  // Terminal state with an operator comment, stored on the assignment record —
  // separate from the review verdict, which is about whether the AI was right.
  const applyResolution = async (status, names, note) => {
    if (!names.length) return;
    setBusy(true);
    try {
      const r = names.length === 1
        ? await apiFetch(`/alerts/${encodeURIComponent(names[0])}/resolve`, { method: 'POST', json: { status, note } })
        : await apiFetch('/alerts/bulk-resolve', { method: 'POST', json: { ids: names, status, note } });
      if (!r.ok) throw new Error();
      await loadAssignments();
      const done = new Set(names);
      setAlerts(list => list.map(a => (done.has(a.filename) ? { ...a, resolution: status } : a)));
      setChecked(new Set());
      toast(status ? `${names.length} marked ${status}.` : `${names.length} reopened.`,
            status === 'resolved' ? 'ok' : status === 'ignored' ? 'warn' : undefined);
      // The status filter is showing a set these alerts just left — refetch.
      if (resFilter) load(false);
      loadSummary(from.replace(/-/g, ''), to.replace(/-/g, ''), vdtFilter, resFilter);
    } catch { toast('Failed to update status.', 'err'); }
    finally { setBusy(false); setResolveOn(null); }
  };

  // Open the comment popup for a resolve/ignore over some set of alerts.
  const askResolve = (status, names, scope) => {
    if (!names.length) { toast('Nothing selected.', 'warn'); return; }
    setResolveOn({ status, names, scope });
  };

  const askResolveFiltered = async (status) => {
    setBusy(true);
    let names = [];
    try { names = await filteredFilenames(); }
    catch { toast('Could not list the filtered alerts.', 'err'); return; }
    finally { setBusy(false); }
    askResolve(status, names, scopeLabel());
  };

  const exportFiltered = async () => {
    let names = [];
    setDlBusy(true);
    try { names = await filteredFilenames(); }
    catch { toast('Could not list the filtered alerts.', 'err'); return; }
    finally { setDlBusy(false); }
    await exportLogs(names, [modFilter || sevFilter || 'all', vdtFilter || 'anyverdict'].join('_'));
  };

  // Dispatch a contextual action id to its real backend call.
  const doAction = (id, a) => {
    if (id === 'confirm')   return setVerdict(a, 'correct');
    if (id === 'dismiss')   return setVerdict(a, 'false_alert');
    if (id === 'not_match') return setVerdict(a, 'wrong_banned');
    // Still false — the alert should not have fired — but a person WAS in frame, so
    // the reason marks it for exclusion when tuning a confidence bar. Was its own
    // verdict; now a reason, so "false" stays one filter instead of two.
    if (id === 'wrong_obj') return setVerdict(a, 'false_alert', 'wrongobj');
    if (id === 'fc_vis')     return setVerdict(a, 'false_alert', 'fcv_vis');
    if (id === 'fc_allowed') return setVerdict(a, 'false_alert', 'fcv_allowed');
    if (id === 'fc_back')    return setVerdict(a, 'false_alert', 'fcv_back');
  };

  const acknowledge = async (a) => {
    if (!a) return;
    setBusy(true);
    try {
      const r = await apiFetch(`/alerts/${encodeURIComponent(a.filename)}/acknowledge`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error();
      setAssignments(m => ({ ...m, [a.filename]: d.assignment }));
      toast('Acknowledged — assigned to you.');
    } catch {
      toast('Failed to acknowledge.', 'err');
    } finally { setBusy(false); }
  };

  const escalate = async (a, userId, userName) => {
    if (!a || !userId) return;
    setBusy(true);
    try {
      const r = await apiFetch(`/alerts/${encodeURIComponent(a.filename)}/escalate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: String(userId), to_name: userName || '', label: a.label || '', cam_name: a.cam_name || '' }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error();
      setAssignments(m => ({ ...m, [a.filename]: d.assignment }));
      toast(`Escalated to ${userName || userId}.`, 'warn');
    } catch {
      toast('Failed to escalate.', 'err');
    } finally { setBusy(false); }
  };

  // What the detail column used to show under Telemetry / Audit trail, reused
  // verbatim behind the row's ⓘ on mobile so nothing is lost with the column.
  const renderInfo = (a) => {
    const an = assignments[a.filename];
    return (
      <>
        <div className="ind-card">
          <KV k="Camera" v={a.cam_name || '—'} />
          <KV k="Label" v={displayLabel(a)} />
          {!a.is_camera_offline && <KV k="Verdict" v={a.verdict || 'unreviewed'} />}
          <KV k="Time" v={fmtAlertTs(a.ts || a.timestamp)} />
          {a.devices_fired && <KV k="Devices" v="🔊 Activated (siren / light / relay)" cls="hot" />}
        </div>
        {(a.detector === 'known' || a.detector === 'banned') && (
          <FaceMatchEvidence telemetry={a.telemetry} />
        )}
        {a.telemetry && typeof a.telemetry === 'object' && (
          <div className="ind-card">
            <div className="ind-eye" style={{ marginBottom: 8 }}>Telemetry</div>
            {Object.entries(a.telemetry).slice(0, 14).map(([k, v]) => (
              <KV key={k} k={k} v={fmtTelemetry(v)} />
            ))}
          </div>
        )}
        <div className="ind-card">
          <div className="ind-eye" style={{ marginBottom: 8 }}>Audit trail</div>
          {auditTrail(a, an, userName).map((e, i) => (
            <div key={i} className="ind-kv">
              <span className="k">{e.t}</span>
              <span className="v" style={e.hot ? { color: 'var(--ind-crit)' } : e.ok ? { color: 'var(--ind-ok)' } : {}}>{e.msg}</span>
            </div>
          ))}
        </div>
      </>
    );
  };

  // Everything RowActions needs, in one bag — the row is rendered inside a map
  // so passing twenty props per alert would be worse than passing this.
  const rowApi = {
    users, sirenOn, lightOn, speakText, setSpeakText, speaking, recording, micError,
    toggleRecord, speakTyped, toggleDevice, downloadAsset, addToKnown, addToBanned,
    acknowledge, escalate, doAction, userName, displayLabel, moduleOf, reviewActions,
    renderInfo,
    resolve: (a) => askResolve('resolved', [a.filename], displayLabel(a)),
    ignore:  (a) => askResolve('ignored',  [a.filename], displayLabel(a)),
    reopen:  (a) => applyResolution(null, [a.filename], null),
  };

  const subtitle = loading ? 'LOADING…'
    : error ? error
    : `${summary.total} EVENTS · ${from} → ${to}`;

  return (
    <IndustrialLayout eyebrow="Security operations" title="Ops Console" subtitle={subtitle} split
      right={
        <>
          <button className="ind-btn" onClick={refreshNow} disabled={refreshing} title="Refresh now">
            {refreshing ? '⏳' : '↻'} Refresh
          </button>
          <button className="ind-btn" onClick={() => navigate('/alerts')} title="Full alerts view">
            🚨 Alerts
          </button>
        </>
      }
      toolbar={
      <>
       {/* One horizontal strip: dates · severity · filters · whole-set actions.
           These used to sit above the queue inside the 340px column, where every
           filter that appeared stole rows from the list it was filtering. */}
       {/* ONE row on a phone: dates · severity · Filters. The three severity tiles
           are 300px of chrome between them, which on a 390px screen pushed the date
           range and the Filters toggle onto lines of their own — three lines of
           toolbar above a queue that only had room for one card. */}
       <div className="ind-toolrow">
        {/* Two native date inputs are ~215px of a 362px phone row — with the
            severity control and the Filters toggle beside them the row wrapped
            to three lines. On mobile the range becomes one button over a sheet
            (which also gets presets and full-width inputs you can actually tap). */}
        {isMobile ? (
          <button className="ind-datebtn" onClick={() => setDatesOpen(true)}
            title="Change the date range">📅 {rangeLabel(from, to)}</button>
        ) : (
          <div className="ind-dates">
            <input type="date" value={from} max={to} onChange={e => setFrom(e.target.value)}
              style={{ padding: '5px 7px', borderRadius: 6, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)', fontSize: 12 }} />
            <span style={{ color: 'var(--ind-tx3)' }}>→</span>
            <input type="date" value={to} min={from} onChange={e => setTo(e.target.value)}
              style={{ padding: '5px 7px', borderRadius: 6, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)', fontSize: 12 }} />
          </div>
        )}
        <div className="ind-toolsep" />
        {/* A <select> is as wide as its widest option, so the mobile severity
            labels are abbreviated — "CRITICAL · 12" alone costs 130px of the row. */}
        {isMobile ? (
          <select className="ind-sevsel" value={sevFilter || ''} aria-label="Severity"
            onChange={e => setSev(e.target.value || null)}>
            <option value="">All · {summary.total}</option>
            <option value="critical">CRIT · {counts.critical}</option>
            <option value="high">HIGH · {counts.high}</option>
            <option value="info">INFO · {counts.info}</option>
          </select>
        ) : (
          <div className="ind-tiles">
            {['critical', 'high', 'info'].map(k => (
              <button key={k} className={`ind-tile ${k === 'critical' ? 'crit' : k}`}
                aria-pressed={sevFilter === k}
                onClick={() => setSev(sevFilter === k ? null : k)}>
                <b>{counts[k]}</b><span>{k.toUpperCase()}</span>
              </button>
            ))}
          </div>
        )}

        {/* Filter bar. COLLAPSED = this toggle and nothing else; every other
            control — the filter groups AND the whole-set actions — lives inside
            the panel, so applying a filter never grows the chrome. */}
        <div className="ind-filterbar">
          <button className="ind-filtertoggle" aria-expanded={filtersOpen}
            onClick={() => setFiltersOpen(o => !o)}>
            {filtersOpen ? '▾' : '▸'} Filters{activeFilters.length ? ` · ${activeFilters.length}` : ''}
          </button>
        </div>

       </div>

       {/* Active filters as removable chips — their own line, and only when there
           ARE any, so an unfiltered board is a single toolbar row. Still always
           visible, so a filtered board is never mistaken for an empty one. */}
       {activeFilters.length > 0 && (
        <div className="ind-chiprow">
          {activeFilters.map(f => (
            <button key={f.key} className="ind-chip" title="Remove this filter" onClick={f.clear}>
              {f.label} <b>✕</b>
            </button>
          ))}
          {activeFilters.length > 1 && (
            <button className="ind-chip"
              onClick={() => { setSev(null); setMod(null); setAsg(''); setCam(''); setVdt(''); setRes(''); setBell(false); }}>
              Clear all <b>✕</b>
            </button>
          )}
        </div>
       )}

       {filtersOpen && (
        <div className="ind-filters">
          <div className="ind-fgroup">
            <div className="ind-rail">By module</div>
            <div className="ind-pills">
              {Object.keys(moduleCounts).sort((a, b) => moduleCounts[b] - moduleCounts[a]).map(k => (
                <button key={k} className={`ind-pill${modFilter === k ? ' on' : ''}`}
                  onClick={() => setMod(modFilter === k ? null : k)}>
                  {MODULE_LABELS[k] || k} <b>{moduleCounts[k]}</b>
                </button>
              ))}
            </div>
          </div>

          {/* Server-side (?verdict=), so "Unreviewed" is the real outstanding queue
              across the whole range, not just the loaded page. */}
          <div className="ind-fgroup">
            <div className="ind-rail">By review</div>
            <div className="ind-pills">
              {REVIEW_FILTERS.map(v => (
                <button key={v.value} className={`ind-pill${vdtFilter === v.value ? ' on' : ''}`}
                  onClick={() => setVdt(vdtFilter === v.value ? '' : v.value)}>
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          {/* The incident's terminal state (server-side ?resolution=). */}
          <div className="ind-fgroup">
            <div className="ind-rail">By status</div>
            <div className="ind-pills">
              {STATUS_FILTERS.map(s => (
                <button key={s.value} className={`ind-pill${resFilter === s.value ? ' on' : ''}`}
                  onClick={() => setRes(resFilter === s.value ? '' : s.value)}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Only where a doorbell has rung in this range (or the filter is on) — a site
              without a Doorbell camera never sees the option. */}
          {(bellFilter || alerts.some(a => a.doorbell)) && (
            <div className="ind-fgroup">
              <div className="ind-rail">Doorbell</div>
              <div className="ind-pills">
                <button className={`ind-pill${bellFilter ? ' on' : ''}`} onClick={() => setBell(b => !b)}>
                  🔔 Doorbell only
                </button>
              </div>
            </div>
          )}

          {cameraList.length > 1 && (
            <div className="ind-fgroup">
              <div className="ind-rail">By camera</div>
              <div className="ind-pills">
                {cameraList.map(cn => (
                  <button key={cn} className={`ind-pill${camFilter === cn ? ' on' : ''}`}
                    onClick={() => setCam(camFilter === cn ? '' : cn)}>
                    🎥 {cn}
                  </button>
                ))}
              </div>
            </div>
          )}

          {Object.keys(assigneeCounts).length > 0 && (
            <div className="ind-fgroup">
              <div className="ind-rail">Assignee</div>
              <div className="ind-pills">
                {Object.keys(assigneeCounts).sort((a, b) => assigneeCounts[b] - assigneeCounts[a]).map(id => (
                  <button key={id} className={`ind-pill${asgFilter === id ? ' on' : ''}`}
                    onClick={() => setAsg(asgFilter === id ? '' : id)}>
                    👤 {userName(id)} <b>{assigneeCounts[id]}</b>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Bulk review and close-out over EVERYTHING matching the filters, not just
              the loaded page. Inside the panel, not on the bar: these appear when you
              open Filters, so setting a filter never costs the list two more rows. */}
          <div className="ind-fgroup" style={{ flex: '1 1 100%' }}>
            <div className="ind-rail">Apply to all {summary.total} matching</div>
            <div className="ind-pills">
              <button className="ind-pill" disabled={busy} onClick={() => bulkVerdictFiltered('correct')}
                title="Mark every alert matching these filters as a real event"
                style={{ borderColor: '#22c55e', color: '#15803d' }}>✓ All real</button>
              <button className="ind-pill" disabled={busy} onClick={() => bulkVerdictFiltered('false_alert')}
                title="Mark every alert matching these filters as a false alert"
                style={{ borderColor: '#ef4444', color: '#b91c1c' }}>✗ All false</button>
              <button className="ind-pill" disabled={busy} onClick={() => askResolveFiltered('resolved')}
                title="Close every alert matching these filters with a comment"
                style={{ borderColor: '#3B4A6B', color: '#3B4A6B' }}>✓ Resolve all…</button>
              <button className="ind-pill" disabled={busy} onClick={() => askResolveFiltered('ignored')}
                title="Dismiss every alert matching these filters — no action needed">⊘ Ignore all…</button>
              <button className="ind-pill" disabled={busy} onClick={deleteFiltered}
                title="Delete every alert matching the current date + module/severity filter"
                style={{ color: '#ef4444', borderColor: '#ef4444' }}>🗑 Delete filtered</button>
              {/* Export sat on the toolbar permanently and cost the bar a slot at
                  every width — on a phone it pushed the date range onto its own
                  line. It operates on the filtered set, so it belongs with the
                  other whole-set actions, behind the same Filters toggle. */}
              <button className="ind-pill" disabled={dlBusy} onClick={exportFiltered}
                title="Download the full log record (telemetry, config snapshot, verdict) for every alert matching the current filters">
                {dlBusy ? '⏳' : '⬇'} Export JSON
              </button>
            </div>
          </div>
        </div>
       )}
      </>
      }>
      {/* ── Triage column: selection rail over a list that scrolls ── */}
      <div className="ind-triage">
       <div className="ind-triage-head">
        <div className="ind-rail" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '0 0 6px' }}>
          {rows.length > 0 && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }} title="Select all shown">
              <input type="checkbox" checked={allShownChecked} onChange={toggleAllShown} />
              {checkedShown > 0 ? `${checkedShown} selected` : `${rows.length} shown`}
            </label>
          )}
          {checkedShown > 0 && (
            <>
              <button className="ind-pill" disabled={busy}
                onClick={() => bulkVerdict('correct', rows.filter(r => checked.has(r.filename)).map(r => r.filename), `${checkedShown} selected`)}
                style={{ borderColor: '#22c55e', color: '#15803d' }}>✓ Real</button>
              <button className="ind-pill" disabled={busy}
                onClick={() => bulkVerdict('false_alert', rows.filter(r => checked.has(r.filename)).map(r => r.filename), `${checkedShown} selected`)}
                style={{ borderColor: '#ef4444', color: '#b91c1c' }}>✗ False</button>
              <button className="ind-pill" disabled={busy}
                onClick={() => askResolve('resolved', rows.filter(r => checked.has(r.filename)).map(r => r.filename), `${checkedShown} selected`)}
                style={{ borderColor: '#3B4A6B', color: '#3B4A6B' }}>✓ Resolve…</button>
              <button className="ind-pill" disabled={busy}
                onClick={() => askResolve('ignored', rows.filter(r => checked.has(r.filename)).map(r => r.filename), `${checkedShown} selected`)}>⊘ Ignore…</button>
              <button className="ind-pill" disabled={dlBusy}
                onClick={() => exportLogs(rows.filter(r => checked.has(r.filename)).map(r => r.filename), 'selected')}>
                {dlBusy ? '⏳' : '⬇'} Logs
              </button>
              <button className="ind-pill" disabled={busy} onClick={deleteChecked}
                style={{ color: '#ef4444', borderColor: '#ef4444' }}>🗑 Delete {checkedShown}</button>
            </>
          )}
        </div>
       </div>

       {/* Only the list scrolls, so the queue is always visible under the head. */}
       <div className="ind-triage-list" ref={triageRef}
         onScroll={e => { const el = e.currentTarget; if (el.scrollHeight - el.scrollTop - el.clientHeight < 300) loadMore(); }}>
        {loading && <div className="ind-empty">Loading alerts…</div>}
        {!loading && !rows.length && <div className="ind-empty">No alerts for {from === to ? from : `${from} → ${to}`}{(sevFilter || modFilter || asgFilter || camFilter || vdtFilter || resFilter || bellFilter) ? ' with these filters' : ''}.</div>}

        {rows.map(a => {
          const an = assignments[a.filename];
          // Status first — a closed incident should read as closed at a glance,
          // whatever its ownership or verdict.
          const statusText = an?.resolution ? STATUS_LABEL[an.resolution]
            : an?.acknowledged_by ? `👤 ${userName(an.acknowledged_by)}`
            : an?.escalated_to ? `↗ ${an.escalated_to_name || userName(an.escalated_to)}`
            : a.verdict ? `verdict: ${a.verdict}` : 'unreviewed';
          // On a phone only the alert being WORKED opens out to the full-width
          // image and its controls. Every card at full size meant ~1.4 cards per
          // screen, so scanning a 40-alert queue was all scrolling.
          const open = isMobile && a.filename === sel;
          return (
          <article key={a.filename}
            className={`ind-row${a.filename === sel ? ' on' : ''}${(an?.acknowledged_by || an?.resolution) ? ' done' : ''}${open ? ' open' : ''}`}
            onClick={() => setSel(a.filename)}>
            <input type="checkbox" title="Select for bulk delete"
              checked={checked.has(a.filename)}
              onClick={e => e.stopPropagation()}
              onChange={() => setChecked(prev => { const s = new Set(prev); s.has(a.filename) ? s.delete(a.filename) : s.add(a.filename); return s; })}
              style={{ alignSelf: 'center', flex: 'none', margin: 0, cursor: 'pointer' }} />
            <div className="thumb">{a.is_camera_offline ? <div className="cam-off">📴</div> : <AlertImage filename={a.filename} />}</div>
            <div className="m">
              <div className="tl">
                <span className={`ind-tag ${a.sev}`}>{displayLabel(a).slice(0, 22).toUpperCase()}</span>
                {a.doorbell && <span className="ind-dev" title="This alert rang the doorbell" aria-label="Doorbell">🔔</span>}
                {a.devices_fired && <span className="ind-dev" title="Devices activated (siren / light / relay)">🔊 DEVICES</span>}
                <span className="ind-ago">{agoLabel(a.ts || a.timestamp)}</span>
              </div>
              {/* Two text rows on a phone, four on desktop. The <h3> is the same
                  string as the tag above it (both are displayLabel), so on mobile
                  the duplicate goes and the camera joins the status on one line. */}
              {isMobile ? (
                <div className="dat">{(a.cam_name || 'unknown cam').toUpperCase()} · {statusText}</div>
              ) : (
                <>
                  <h3>{displayLabel(a)}</h3>
                  <div className="dat">{statusText}</div>
                  <div className="cam">{(a.cam_name || 'unknown cam').toUpperCase()}</div>
                </>
              )}
              {/* Mobile has no detail column, so the alert carries its own
                  controls. Desktop keeps the side panel and renders none of
                  this, so the row stays a one-glance triage line. */}
              {open && (
                <RowActions a={a} asn={an} busy={busy} api={rowApi} />
              )}
            </div>
          </article>
          );
        })}

        {!loading && rows.length > 0 && (
          loadingMore ? <div className="ind-empty" style={{ padding: 16 }}>Loading more…</div>
          : hasMore ? <button className="ind-btn" style={{ width: '100%', marginTop: 4 }} onClick={loadMore}>Load more</button>
          : <div className="ind-empty" style={{ padding: 12, fontSize: 11 }}>— end of alerts —</div>
        )}
       </div>
      </div>

      {/* ── Detail column — desktop only. On a phone the grid collapsed to one
           column and this stacked below a 45vh list, so acting on the row you
           just tapped meant scrolling past the whole queue to find it. ── */}
      <div className="ind-detail" hidden={isMobile}>
        {isMobile ? null : !active ? (
          <div className="ind-empty">Select an event to inspect.</div>
        ) : (
          <>
            <div className="ind-dhead">
              <div>
                <div className="ind-eye">{(active.cam_name || '').toUpperCase()}</div>
                <h2>{displayLabel(active)}{active.doorbell && <span title="This alert rang the doorbell" aria-label="Doorbell"> 🔔</span>}</h2>
                <div className="id">{active.filename}</div>
              </div>
              <span className={`ind-tag ${active.sev}`} style={{ marginLeft: 'auto' }}>{active.sev.toUpperCase()}</span>
            </div>

            <div className="ind-dgrid">
              {active.is_camera_offline ? (
                <div className="ind-media">
                  <div className="ind-card">
                    <div className="ind-eye" style={{ marginBottom: 8 }}>
                      Cameras offline ({(active.cameras || []).length})
                    </div>
                    {(active.cameras || []).length > 5 && (
                      <CamOfflineFilterCombo cameras={active.cameras} selected={camOffSelected} onChange={setCamOffSelected} />
                    )}
                    {(active.cameras || [])
                      .filter(c => camOffSelected.size === 0 || camOffSelected.has(c.cam_idx))
                      .map(c => (
                        <KV key={c.cam_idx} k={c.cam_name} v={`offline ${agoLabel(c.since_ts)}`} cls="hot" />
                      ))}
                  </div>
                </div>
              ) : (
              <div className="ind-media">
                <div className="ind-frame" style={{ cursor: showVideo ? 'default' : 'zoom-in' }}
                  onClick={() => { if (!showVideo) setLightbox(active.filename); }}>
                  {showVideo ? <AlertVideo filename={active.filename} /> : <AlertImage filename={active.filename} />}
                </div>
                <div className="ind-strip">
                  <div className="f" style={{ cursor: 'zoom-in' }} onClick={() => setLightbox(active.filename)}><AlertImage filename={active.filename} /></div>
                  <button className={`clip${showVideo ? ' on' : ''}`} onClick={() => setVid(v => !v)}>
                    {showVideo ? '🖼 Photo' : '▶ Clip'}
                  </button>
                  <button className="clip" disabled={busy} title="Download snapshot"
                    onClick={() => downloadAsset(active, 'image')}>⬇ Photo</button>
                  <button className="clip" disabled={busy} title="Download clip video"
                    onClick={() => downloadAsset(active, 'video')}>⬇ Clip</button>
                </div>
              </div>
              )}

              <div className="ind-side">
            {/* Three labelled groups, in the order an operator works an incident:
                ACTIONS (who owns it) → REVIEW (was it real) → RESPONSE (hardware).
                Delete lives in the triage column with the other destructive bulk
                operations — it is not part of working an incident. */}
            <div className="ind-rail" style={{ margin: '0 0 8px' }}>
              Actions{asn?.resolution ? ` · ${STATUS_LABEL[asn.resolution]}` : ''}
            </div>
            <div className="ind-acts">
              {/* Terminal state. Once closed the pair collapses to a single Reopen,
                  so a closed incident can't be quietly re-closed a different way. */}
              {asn?.resolution ? (
                <button className="ind-act wide" disabled={busy}
                  onClick={() => applyResolution(null, [active.filename], null)}>
                  ↺ Reopen incident
                </button>
              ) : (
                <>
                  <button className="ind-act pri" disabled={busy}
                    onClick={() => askResolve('resolved', [active.filename], displayLabel(active))}>✓ Resolve…</button>
                  <button className="ind-act" disabled={busy}
                    onClick={() => askResolve('ignored', [active.filename], displayLabel(active))}>⊘ Ignore…</button>
                </>
              )}
              <button className="ind-act pri wide" disabled={busy || !!asn?.acknowledged_by} onClick={() => acknowledge(active)}>
                {asn?.acknowledged_by ? `✓ Acked · ${userName(asn.acknowledged_by)}` : '✓ Acknowledge (assign to me)'}
              </button>
              <select className="ind-act warn wide" disabled={busy} value=""
                onChange={e => { const u = users.find(x => String(x.id) === e.target.value); if (u) escalate(active, u.id, u.username || u.id); }}>
                <option value="">{asn?.escalated_to ? `↗ ${asn.escalated_to_name || userName(asn.escalated_to)} — reassign…` : '↗ Escalate to…'}</option>
                {users.length === 0 && <option value="" disabled>No users — add them in Users &amp; Roles</option>}
                {users.map(u => (
                  <option key={u.id} value={u.id}>{(u.username || `User ${u.id}`)}{u.isAdmin ? ' (admin)' : ''}</option>
                ))}
              </select>
              {/* Enrolment needs a saved face crop — only offered when one exists.
                  Two explicit targets; each opens the popup already on its tab. */}
              {active.has_face && (
                <>
                  <button className="ind-act ok" disabled={busy}
                    onClick={() => addToKnown(active)}>👤 Add to Known</button>
                  <button className="ind-act danger" disabled={busy}
                    onClick={() => addToBanned(active)}>🚫 Add to Banned</button>
                </>
              )}
            </div>

            {/* Neither section applies to the synthetic camera-offline row: there is
                no AI verdict to review, and no camera feed to sound a siren/light at. */}
            {!active.is_camera_offline && (
              <>
              {/* Review — the verdict. Feeds threshold tuning and the JSON export, so
                  it is a separate decision from acknowledging the incident. */}
              <div className="ind-rail" style={{ margin: '2px 0 8px' }}>
                Review{active.verdict ? ` · ${active.verdict.replace(/_/g, ' ')}` : ''}
              </div>
              <div className="ind-acts">
                {reviewActions(moduleOf(active)).map(act => (
                  <button key={act.id} className={`ind-act ${act.cls}`}
                    disabled={busy
                      || (act.id === 'confirm' && active.verdict === 'correct')
                      || ((act.id === 'dismiss' || act.id === 'not_match' || act.id === 'wrong_obj')
                          && active.verdict && active.verdict !== 'correct')}
                    onClick={() => doAction(act.id, active)}>{act.label}</button>
                ))}
              </div>

              {/* Physical response — sound + light as ON/OFF toggles (a button that
                  reports the state it is in, not a pair that leaves you guessing which
                  one is live), plus Speak: the same record-and-send / type-to-speak
                  path as the mobile dashboard, so an operator can talk to the site. */}
              <div className="ind-rail" style={{ margin: '2px 0 8px' }}>Physical response</div>
              <div className="ind-acts">
                <button className={`ind-act${sirenOn ? ' danger' : ''}`} disabled={busy}
                  aria-pressed={sirenOn} title={sirenOn ? 'Sound is on — tap to stop' : 'Tap to sound the warning'}
                  onClick={() => toggleDevice('siren', !sirenOn)}>
                  {sirenOn ? '🔊 Sound ON' : '🔇 Sound OFF'}
                </button>
                <button className={`ind-act${lightOn ? ' warn' : ''}`} disabled={busy}
                  aria-pressed={lightOn} title={lightOn ? 'Light is on — tap to switch off' : 'Tap to switch the light on'}
                  onClick={() => toggleDevice('light', !lightOn)}>
                  {lightOn ? '💡 Light ON' : '🌙 Light OFF'}
                </button>
                <button className={`ind-act wide${recording ? ' danger' : ''}`} disabled={speaking}
                  onClick={toggleRecord}>
                  {recording ? '⏹ Stop & Send' : '🎙 Speak (record voice)'}
                </button>
              </div>
              {micError && (
                <div style={{ fontSize: 11.5, color: 'var(--ind-crit)', margin: '-6px 0 10px', lineHeight: 1.4 }}>⚠ {micError}</div>
              )}
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <input type="text" placeholder="…or type text to speak aloud"
                  value={speakText} disabled={recording}
                  onChange={e => setSpeakText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') sayText(); }}
                  style={{ flex: 1, minWidth: 0, padding: '9px 11px', borderRadius: 9, fontSize: 12.5,
                           border: '1px solid var(--ind-line)', background: 'var(--ind-ink)',
                           color: 'var(--ind-tx)', outline: 'none', opacity: recording ? 0.4 : 1 }} />
                <button className="ind-act" style={{ width: 'auto', flex: '0 0 auto' }}
                  disabled={speaking || recording || !speakText.trim()} onClick={sayText}>
                  {speaking ? '…' : '🔊 Speak'}
                </button>
              </div>
              </>
            )}

            {(asn?.acknowledged_by || asn?.escalated_to || asn?.resolution) && (
              <div className="ind-card">
                <div className="ind-eye" style={{ marginBottom: 8 }}>Incident ownership</div>
                {asn.acknowledged_by && <KV k="Acknowledged by" v={`${userName(asn.acknowledged_by)}${asn.acknowledged_at ? ` · ${asn.acknowledged_at}` : ''}`} cls="ok" />}
                {asn.escalated_to && <KV k="Escalated to" v={`${asn.escalated_to_name || userName(asn.escalated_to)}${asn.escalated_at ? ` · ${asn.escalated_at}` : ''}`} cls="hot" />}
                {asn.resolution && (
                  <>
                    <KV k="Status" v={`${STATUS_LABEL[asn.resolution]} by ${userName(asn.resolved_by)}${asn.resolved_at ? ` · ${asn.resolved_at}` : ''}`} cls="ok" />
                    {asn.resolution_note && <KV k="Comment" v={asn.resolution_note} />}
                  </>
                )}
              </div>
            )}

            <div className="ind-card">
              <KV k="Camera" v={active.cam_name || '—'} />
              <KV k="Label" v={displayLabel(active)} />
              {!active.is_camera_offline && <KV k="Verdict" v={active.verdict || 'unreviewed'} cls={active.verdict === 'correct' ? 'ok' : active.verdict === 'false_alert' ? 'hot' : ''} />}
              <KV k="Time" v={fmtAlertTs(active.ts || active.timestamp)} />
              {active.devices_fired && <KV k="Devices" v="🔊 Activated (siren / light / relay)" cls="hot" />}
            </div>

            {(active.detector === 'known' || active.detector === 'banned') && (
              <FaceMatchEvidence telemetry={active.telemetry} />
            )}

            {active.telemetry && typeof active.telemetry === 'object' && (
              <div className="ind-card">
                <div className="ind-eye" style={{ marginBottom: 8 }}>Telemetry</div>
                {Object.entries(active.telemetry).slice(0, 14).map(([k, v]) => (
                  <KV key={k} k={k} v={fmtTelemetry(v)} />
                ))}
              </div>
            )}

            <div className="ind-card">
              <div className="ind-eye" style={{ marginBottom: 8 }}>Audit trail</div>
              {auditTrail(active, asn, userName).map((e, i) => (
                <div key={i} className="ind-kv">
                  <span className="k">{e.t}</span>
                  <span className="v" style={e.hot ? { color: 'var(--ind-crit)' } : e.ok ? { color: 'var(--ind-ok)' } : {}}>{e.msg}</span>
                </div>
              ))}
            </div>
              </div>
            </div>
          </>
        )}
      </div>

      {lightbox && (
        <div onClick={() => setLightbox(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,.88)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, cursor: 'zoom-out' }}>
          <LightboxImage filename={lightbox} />
          <button onClick={e => { e.stopPropagation(); setLightbox(null); }}
            style={{ position: 'absolute', top: 16, right: 20, background: 'transparent', border: 'none', color: '#fff', fontSize: 26, cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {datesOpen && (
        <OpsModal title="Date range" onClose={() => setDatesOpen(false)}>
          <div className="ind-pills" style={{ marginBottom: 14 }}>
            {[['Today', 0, 0], ['Yesterday', 1, 1], ['Last 7 days', 6, 0], ['Last 30 days', 29, 0]].map(([lbl, back, end]) => (
              <button key={lbl} className="ind-pill"
                onClick={() => { setFrom(daysAgo(back)); setTo(daysAgo(end)); setDatesOpen(false); }}>{lbl}</button>
            ))}
          </div>
          <label className="ind-rail" style={{ margin: '0 0 6px' }}>From</label>
          <input type="date" value={from} max={to} onChange={e => setFrom(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 11px', borderRadius: 9, fontSize: 13,
                     border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)' }} />
          <label className="ind-rail" style={{ margin: '12px 0 6px' }}>To</label>
          <input type="date" value={to} min={from} onChange={e => setTo(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 11px', borderRadius: 9, fontSize: 13,
                     border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)' }} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
            <button className="ind-act pri" style={{ width: 'auto', padding: '0 16px' }}
              onClick={() => setDatesOpen(false)}>Done</button>
          </div>
        </OpsModal>
      )}

      {enroll && (
        <EnrollModal filename={enroll.filename} initialMode={enroll.mode} onClose={() => setEnroll(null)} />
      )}

      {resolveOn && (
        <ResolveModal
          status={resolveOn.status} names={resolveOn.names} scope={resolveOn.scope} busy={busy}
          onConfirm={note => applyResolution(resolveOn.status, resolveOn.names, note)}
          onClose={() => setResolveOn(null)} />
      )}
    </IndustrialLayout>
  );
}

// Known / Banned face enrollment popup — pick an existing person or enter a new
// name; enrolls this alert's face via the same /faces/*/from_alert endpoints the
// main Alerts screen uses.
function EnrollModal({ filename, initialMode, onClose }) {
  // Mode is FIXED by the panel button that opened this ("Add to Known" / "Add to
  // Banned") — mirrors the main Alerts flow where the face-preview buttons pick the
  // target, so there are no in-modal Known/Banned tabs to get out of sync.
  const mode    = initialMode || 'known';
  const isKnown = mode === 'known';
  const accent  = isKnown ? '#22c55e' : '#ef4444';

  const [step,    setStep]    = useState('pick');   // 'pick' | 'new'
  const [persons, setPersons] = useState([]);
  const [name,    setName]    = useState('');
  const [profile, setProfile] = useState('day');
  const [busy,    setBusy]    = useState(false);
  const [err,     setErr]     = useState('');

  // Detected-face preview — same crop + metadata the main Alerts page shows, so the
  // operator confirms exactly whose face they are enrolling. Keyed by alert id
  // (filename minus extension); face jpg + meta json are sidecars of the alert.
  const alertId = filename.replace(/\.(jpg|jpeg|png)$/i, '');
  const [faceSrc, setFaceSrc] = useState(null);
  const [meta,    setMeta]    = useState(null);
  useEffect(() => {
    let live = true;
    apiImageUrl(`/alerts/faces/${alertId}_face.jpg`).then(u => { if (live) setFaceSrc(u); }).catch(() => {});
    apiFetch(`/alerts/${encodeURIComponent(alertId)}/face/meta`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (live) setMeta(d); })
      .catch(() => {});
    return () => { live = false; };
  }, [alertId]);

  // Existing contacts for this mode.
  useEffect(() => {
    let live = true;
    apiFetch(isKnown ? '/faces/known' : '/faces/banned')
      .then(r => r.ok ? r.json() : { persons: [] })
      .then(d => { if (live) setPersons((d.persons || []).map(p => p.name).sort()); })
      .catch(() => { if (live) setPersons([]); });
    return () => { live = false; };
  }, [isKnown]);

  const doEnroll = async (who) => {
    const n = (who || name).trim();
    if (!n) { setErr('Pick a person or enter a name.'); return; }
    setBusy(true); setErr('');
    try {
      const url = isKnown
        ? `/faces/known/${encodeURIComponent(n)}/from_alert?filename=${encodeURIComponent(filename)}&profile=${profile}`
        : `/faces/banned/${encodeURIComponent(n)}/from_alert?filename=${encodeURIComponent(filename)}`;
      const r = await apiFetch(url, { method: 'POST' });
      if (!r.ok) { const b = await r.json().catch(() => ({})); setErr(b.detail || 'Enrollment failed — check server.'); setBusy(false); return; }
      toast(`Added to ${isKnown ? 'Known Faces' : 'Banned'} as “${n}”.`, 'ok');
      onClose();
    } catch { setErr('Request failed — check server.'); setBusy(false); }
  };

  const Profile = () => !isKnown ? null : (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
      <span style={{ fontSize: 12, color: 'var(--ind-tx2)' }}>Profile</span>
      {['day', 'night'].map(p => (
        <button key={p} className={`ind-pill${profile === p ? ' on' : ''}`} onClick={() => setProfile(p)}>
          {p === 'day' ? '☀️ Day' : '🌙 Night'}
        </button>
      ))}
    </div>
  );

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 3100, background: 'rgba(0,0,0,.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div className="ind-card" onClick={e => e.stopPropagation()}
        style={{ width: 380, maxWidth: '92vw', maxHeight: '88vh', overflow: 'auto' }}>
        {/* Header — reflects mode + current step */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
          <b style={{ fontSize: 15 }}>
            {step === 'new'
              ? (isKnown ? '👤 New Person' : '🚫 New Entry')
              : (isKnown ? '👤 Mark as Known' : '🚫 Ban Person')}
          </b>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: 'var(--ind-tx3)', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        {/* Detected face + metadata (identical to the main Alerts preview) */}
        <div style={{ width: 160, height: 160, margin: '0 auto 12px', borderRadius: 12,
          overflow: 'hidden', background: 'var(--ind-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {faceSrc
            ? <img src={faceSrc} alt="detected face" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span style={{ color: 'var(--ind-tx3)', fontSize: 12 }}>loading…</span>}
        </div>
        {meta && (
          <div style={{ marginBottom: 14, fontSize: 12.5, color: 'var(--ind-tx2)',
            display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
            <span>Det score</span><strong style={{ color: 'var(--ind-tx)' }}>{meta.det_score}</strong>
            <span>Sharpness</span><strong style={{ color: 'var(--ind-tx)' }}>{meta.sharpness}</strong>
            <span>Camera</span><strong style={{ color: 'var(--ind-tx)' }}>{meta.camera}</strong>
            <span>Time</span><strong style={{ color: 'var(--ind-tx)' }}>{meta.timestamp?.replace('T', ' ')}</strong>
          </div>
        )}

        {/* Pick step — add to existing (avatar rows) + profile + New Person */}
        {step === 'pick' && (
          <>
            {persons.length > 0 && (
              <>
                <div className="ind-rail">Add to existing</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto', margin: '6px 0 12px' }}>
                  {persons.map(p => (
                    <button key={p} disabled={busy} onClick={() => doEnroll(p)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--ind-ink)',
                        border: '1px solid var(--ind-line)', borderRadius: 8, padding: '9px 12px', cursor: 'pointer',
                        color: 'var(--ind-tx)', fontSize: 14, textAlign: 'left' }}>
                      <span style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: accent,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#fff' }}>
                        {p.charAt(0).toUpperCase()}
                      </span>
                      {p}
                    </button>
                  ))}
                </div>
              </>
            )}

            <Profile />

            <button onClick={() => { setStep('new'); setErr(''); }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%',
                background: 'transparent', border: `1px dashed ${accent}`, borderRadius: 8, padding: '10px 14px',
                cursor: 'pointer', color: accent, fontSize: 14, fontWeight: 600 }}>
              ➕ New Person
            </button>

            {err && <div style={{ color: 'var(--ind-crit)', fontSize: 12, marginTop: 10 }}>{err}</div>}
            {busy && <div style={{ color: 'var(--ind-tx3)', fontSize: 12, marginTop: 10, textAlign: 'center' }}>⏳ Enrolling…</div>}
          </>
        )}

        {/* New person step */}
        {step === 'new' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button type="button" onClick={() => { setStep('pick'); setErr(''); setName(''); }}
              style={{ alignSelf: 'flex-start', background: 'transparent', border: 'none', color: 'var(--ind-tx3)', cursor: 'pointer', fontSize: 13, padding: 0 }}>
              ← Back
            </button>
            <input value={name} autoFocus onChange={e => setName(e.target.value)} placeholder="Person name…"
              onKeyDown={e => { if (e.key === 'Enter') doEnroll(); }}
              style={{ padding: '9px 12px', borderRadius: 6, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)' }} />
            <Profile />
            {err && <div style={{ color: 'var(--ind-crit)', fontSize: 12 }}>{err}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="ind-act" onClick={onClose} style={{ width: 'auto', padding: '0 14px' }}>Cancel</button>
              <button className={`ind-act ${isKnown ? 'ok' : 'danger'}`} disabled={busy} onClick={() => doEnroll()} style={{ width: 'auto', padding: '0 14px' }}>
                {busy ? '⏳…' : isKnown ? '✓ Enroll' : '🚫 Ban'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Full-size image for the lightbox.
function LightboxImage({ filename }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let live = true;
    apiImageUrl(`/alerts/${encodeURIComponent(filename)}/image`).then(u => live && setSrc(u));
    return () => { live = false; };
  }, [filename]);
  if (!src) return <div style={{ color: '#fff' }}>loading…</div>;
  return <img src={src} alt="" onClick={e => e.stopPropagation()}
    style={{ maxWidth: '95vw', maxHeight: '92vh', objectFit: 'contain', borderRadius: 8, boxShadow: '0 10px 60px rgba(0,0,0,.6)' }} />;
}

// Derived per-incident audit trail from the alert + its ownership record.
function auditTrail(a, asn, userName) {
  const rows = [];
  const raised = fmtAlertTs(a.ts || a.timestamp);
  rows.push({ t: raised, msg: `Alert raised · ${displayLabel(a)}`, hot: a.sev === 'critical' });
  if (a.verdict) rows.push({ t: '—', msg: a.verdict === 'correct' ? 'Confirmed as real' : `Marked ${a.verdict.replace(/_/g, ' ')}`, ok: a.verdict !== 'correct', hot: a.verdict === 'correct' });
  if (asn?.acknowledged_by) rows.push({ t: asn.acknowledged_at || '—', msg: `Acknowledged by ${userName(asn.acknowledged_by)}`, ok: true });
  if (asn?.escalated_to) rows.push({ t: asn.escalated_at || '—', msg: `Escalated to ${asn.escalated_to_name || userName(asn.escalated_to)}`, hot: true });
  if (asn?.resolution) {
    rows.push({
      t: asn.resolved_at || '—',
      msg: `${asn.resolution === 'resolved' ? 'Resolved' : 'Ignored'} by ${userName(asn.resolved_by)}`
           + (asn.resolution_note ? ` — “${asn.resolution_note}”` : ''),
      ok: true,
    });
  }
  return rows;
}

// Comment popup for closing an incident. Resolve REQUIRES a note (what was done is
// the whole point of the record); Ignore's is optional and offers one-tap presets,
// so dismissing a filter-wide slice of false positives stays a single click.
const IGNORE_PRESETS = [
  'False positive — no one there',
  'Known/authorised activity',
  'Duplicate of another alert',
  'Environmental (weather / light / foliage)',
  'No action needed',
];

// Below this width the console drops the detail column entirely and every
// action lives on the alert row. The split view degraded badly here: the grid
// collapsed to one column, so the list and the detail panel stacked and you
// scrolled past a 45vh list to reach the buttons for the row you had tapped.
// Every per-alert control, as THREE ROWS OF FOUR on the alert itself:
//  1. inspect   Clip · Review · Download · Info
//  2. dispose   Ack · Resolve · Ignore · Escalate
//  3. respond   Sound · Light · Speak · Enrol
// Twelve slots for thirteen controls, so two pairs collapse into one button
// with a two-choice sheet: Download (photo / clip) and Enrol (known / banned).
// Anything else needing more than one control opens a popup rather than
// growing the row: Review (the verdicts), Escalate (the user list), Speak
// (record or type), Info (face match, telemetry, audit trail).
function RowActions({ a, asn, busy, api }) {
  const [pop, setPop] = useState(null);   // 'review' | 'escalate' | 'speak' | 'info' | 'clip' | 'dl' | 'enrol'
  const closed  = !!asn?.resolution;
  const offline = !!a.is_camera_offline;
  const stop    = fn => e => { e.stopPropagation(); fn(); };

  return (
    <div className="ind-rowacts" onClick={e => e.stopPropagation()}>
      {/* INSPECT. Download is one button, not a Photo/Clip pair — it opens a
          two-choice sheet, which keeps the grid at four columns. */}
      <div className="ind-rowact-line">
        {!offline && (
          <Act ic="▶" lbl="Clip" title="Play the alert clip" onClick={stop(() => setPop('clip'))} />
        )}
        {!offline && (
          <Act ic="☑" lbl={a.verdict ? 'Reviewed' : 'Review'} cls={a.verdict ? 'ok' : ''} disabled={busy}
            title={a.verdict ? `Reviewed — ${a.verdict.replace(/_/g, ' ')}` : 'Was this alert real?'}
            onClick={stop(() => setPop('review'))} />
        )}
        {/* A camera-offline row is synthetic — there is no snapshot or clip on
            disk, so the download would only ever fail. */}
        {!offline && (
          <Act ic="⬇" lbl="Download" disabled={busy} title="Download the photo or the clip"
            onClick={stop(() => setPop('dl'))} />
        )}
        <Act ic="ⓘ" lbl="Info" title="Face match, telemetry, audit trail" onClick={stop(() => setPop('info'))} />
      </div>

      {/* DISPOSE. */}
      <div className="ind-rowact-line">
        {closed ? (
          <Act ic="↺" lbl="Reopen" disabled={busy} onClick={stop(() => api.reopen(a))} />
        ) : (
          <>
            <Act ic="✓" lbl={asn?.acknowledged_by ? 'Acked' : 'Ack'} cls="pri"
              disabled={busy || !!asn?.acknowledged_by}
              title={asn?.acknowledged_by ? `Acknowledged by ${api.userName(asn.acknowledged_by)}` : 'Acknowledge — assign to me'}
              onClick={stop(() => api.acknowledge(a))} />
            <Act ic="✓" lbl="Resolve" cls="pri" disabled={busy} title="Close with a comment"
              onClick={stop(() => api.resolve(a))} />
            <Act ic="⊘" lbl="Ignore" disabled={busy} title="Dismiss — no action needed"
              onClick={stop(() => api.ignore(a))} />
            <Act ic="↗" lbl="Escalate" cls="warn" disabled={busy}
              title={asn?.escalated_to ? `Escalated to ${asn.escalated_to_name || api.userName(asn.escalated_to)} — reassign` : 'Escalate to another operator'}
              onClick={stop(() => setPop('escalate'))} />
          </>
        )}
      </div>

      {/* RESPOND. Known/Banned are one Enrol button for the same reason as
          Download: the sheet picks the target, the grid stays at four. */}
      {!offline && (
        <div className="ind-rowact-line">
          <Act ic={api.sirenOn ? '🔊' : '🔇'} lbl={api.sirenOn ? 'Sound ON' : 'Sound'}
            cls={api.sirenOn ? 'danger' : ''} disabled={busy} pressed={api.sirenOn}
            title={api.sirenOn ? 'Sound is on — tap to stop' : 'Sound the warning'}
            onClick={stop(() => api.toggleDevice('siren', !api.sirenOn))} />
          <Act ic={api.lightOn ? '💡' : '🌙'} lbl={api.lightOn ? 'Light ON' : 'Light'}
            cls={api.lightOn ? 'warn' : ''} disabled={busy} pressed={api.lightOn}
            title={api.lightOn ? 'Light is on — tap to switch off' : 'Switch the light on'}
            onClick={stop(() => api.toggleDevice('light', !api.lightOn))} />
          <Act ic="🎙" lbl="Speak" disabled={busy} title="Speak on the site speaker"
            onClick={stop(() => setPop('speak'))} />
          {a.has_face && (
            <Act ic="👤" lbl="Enrol" cls="ok" disabled={busy} title="Add this face to Known or Banned"
              onClick={stop(() => setPop('enrol'))} />
          )}
        </div>
      )}

      {pop === 'dl' && (
        <OpsModal title="Download" onClose={() => setPop(null)}>
          <div className="ind-acts">
            <button className="ind-act" disabled={busy}
              onClick={() => { api.downloadAsset(a, 'image'); setPop(null); }}>🖼 Photo (JPG)</button>
            <button className="ind-act" disabled={busy}
              onClick={() => { api.downloadAsset(a, 'video'); setPop(null); }}>🎬 Clip (MP4)</button>
          </div>
        </OpsModal>
      )}

      {pop === 'enrol' && (
        <OpsModal title="Enrol this face" onClose={() => setPop(null)}>
          <div className="ind-acts">
            <button className="ind-act ok" disabled={busy}
              onClick={() => { api.addToKnown(a); setPop(null); }}>👤 Add to Known</button>
            <button className="ind-act danger" disabled={busy}
              onClick={() => { api.addToBanned(a); setPop(null); }}>🚫 Add to Banned</button>
          </div>
        </OpsModal>
      )}

      {pop === 'review' && (
        <OpsModal title={`Review · ${api.displayLabel(a)}`} onClose={() => setPop(null)}>
          <div className="ind-acts">
            {api.reviewActions(api.moduleOf(a)).map(act => (
              <button key={act.id} className={`ind-act ${act.cls}`}
                disabled={busy
                  || (act.id === 'confirm' && a.verdict === 'correct')
                  || ((act.id === 'dismiss' || act.id === 'not_match' || act.id === 'wrong_obj')
                      && a.verdict && a.verdict !== 'correct')}
                onClick={() => { api.doAction(act.id, a); setPop(null); }}>{act.label}</button>
            ))}
          </div>
        </OpsModal>
      )}

      {pop === 'escalate' && (
        <OpsModal title="Escalate to…" onClose={() => setPop(null)}>
          {api.users.length === 0
            ? <div className="ind-empty">No users — add them in Users &amp; Roles.</div>
            : (
              <div className="ind-acts">
                {api.users.map(u => (
                  <button key={u.id} className="ind-act" disabled={busy}
                    onClick={() => { api.escalate(a, u.id, u.username || u.id); setPop(null); }}>
                    {(u.username || `User ${u.id}`)}{u.isAdmin ? ' (admin)' : ''}
                  </button>
                ))}
              </div>
            )}
        </OpsModal>
      )}

      {pop === 'speak' && (
        <OpsModal title="Speak on site" onClose={() => setPop(null)}>
          {api.micError && <div style={{ fontSize: 12, color: '#b91c1c', marginBottom: 8 }}>{api.micError}</div>}
          <div className="ind-acts">
            <button className={`ind-act${api.recording ? ' danger' : ''}`} onClick={api.toggleRecord}>
              {api.recording ? '⏹ Stop & Send' : '🎙 Record voice'}
            </button>
          </div>
          <textarea rows={3} value={api.speakText} onChange={e => api.setSpeakText(e.target.value)}
            placeholder="…or type a message to speak"
            style={{ width: '100%', boxSizing: 'border-box', padding: '9px 11px', borderRadius: 9,
                     fontSize: 12.5, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)',
                     color: 'var(--ind-tx)', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
            <button className="ind-act" style={{ width: 'auto', padding: '0 14px' }}
              disabled={api.speaking || !api.speakText.trim()}
              onClick={() => api.speakTyped()}>{api.speaking ? '…' : '🔊 Speak'}</button>
          </div>
        </OpsModal>
      )}

      {pop === 'clip' && (
        <OpsModal title={api.displayLabel(a)} onClose={() => setPop(null)} width={720}>
          <div className="ind-frame"><AlertVideo filename={a.filename} /></div>
        </OpsModal>
      )}

      {pop === 'info' && (
        <OpsModal title={api.displayLabel(a)} onClose={() => setPop(null)} width={520}>
          {api.renderInfo(a)}
        </OpsModal>
      )}
    </div>
  );
}

// One button of a row's action grid. The icon and the label are SEPARATE spans so
// that on a screen too narrow for four words (320px — the label needs ~70px and
// gets 64px) CSS can drop the label and leave the icon, rather than ellipsising
// every button into "Downl…". title + aria-label carry the name either way.
function Act({ ic, lbl, cls = '', disabled, pressed, title, onClick }) {
  return (
    <button className={`ind-act ${cls}`} disabled={disabled} onClick={onClick}
      title={title || lbl} aria-label={lbl}
      {...(pressed === undefined ? {} : { 'aria-pressed': pressed })}>
      <span className="ic" aria-hidden="true">{ic}</span>
      <span className="lbl">{lbl}</span>
    </button>
  );
}

const MOBILE_Q = '(max-width: 900px)';

function useIsMobile() {
  const [m, setM] = useState(() => {
    try { return window.matchMedia(MOBILE_Q).matches; } catch { return false; }
  });
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia(MOBILE_Q); } catch { return; }
    const on = e => setM(e.matches);
    // addListener is the Safari < 14 spelling; still in the field on site tablets.
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    setM(mq.matches);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
  }, []);
  return m;
}

// Shared popup shell. The row can only afford one button per concept, so
// anything with more than one control behind it (review verdicts, the user
// list, speak) opens through here.
function OpsModal({ title, onClose, children, width = 440 }) {
  useEffect(() => {
    const esc = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);
  return (
    <div onClick={onClose} role="dialog" aria-modal="true" aria-label={title}
      style={{ position: 'fixed', inset: 0, zIndex: 3100, background: 'rgba(0,0,0,.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="ind-card" onClick={e => e.stopPropagation()}
        style={{ width, maxWidth: '94vw', maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <b style={{ fontSize: 15 }}>{title}</b>
          <button onClick={onClose} aria-label="Close"
            style={{ marginLeft: 'auto', background: 'transparent', border: 'none',
                     color: 'var(--ind-tx3)', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ResolveModal({ status, names, scope, busy, onConfirm, onClose }) {
  const [note, setNote] = useState('');
  const isResolve = status === 'resolved';
  const accent    = isResolve ? '#3B4A6B' : '#9A6520';
  const ready     = !isResolve || note.trim().length > 0;

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 3100, background: 'rgba(0,0,0,.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div className="ind-card" onClick={e => e.stopPropagation()} style={{ width: 440, maxWidth: '92vw' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
          <b style={{ fontSize: 15 }}>{isResolve ? '✓ Resolve incident' : '⊘ Ignore incident'}</b>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: 'var(--ind-tx3)', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--ind-tx2)', marginBottom: 12 }}>
          {names.length === 1 ? scope : `${names.length} alerts · ${scope}`}
        </div>

        {!isResolve && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            {IGNORE_PRESETS.map(p => (
              <button key={p} className={`ind-pill${note === p ? ' on' : ''}`}
                onClick={() => setNote(note === p ? '' : p)}>{p}</button>
            ))}
          </div>
        )}

        <textarea value={note} autoFocus rows={3}
          onChange={e => setNote(e.target.value)}
          placeholder={isResolve ? 'What was done? (required)' : 'Why is this being ignored? (optional)'}
          style={{ width: '100%', boxSizing: 'border-box', padding: '9px 11px', borderRadius: 9, fontSize: 12.5,
                   border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)',
                   outline: 'none', resize: 'vertical', fontFamily: 'inherit' }} />

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button className="ind-act" style={{ width: 'auto', padding: '0 14px' }} onClick={onClose}>Cancel</button>
          <button className="ind-act" disabled={busy || !ready}
            style={{ width: 'auto', padding: '0 14px', background: accent, borderColor: accent, color: '#fff', fontWeight: 700 }}
            onClick={() => onConfirm(note.trim() || null)}>
            {busy ? '⏳…' : isResolve ? `✓ Resolve ${names.length}` : `⊘ Ignore ${names.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Telemetry values are raw floats off the detector — a score arrived as
// "1.122679591178894", which is noise in a review panel. Trim to 3 dp without
// touching integers, strings or booleans.
function fmtTelemetry(v) {
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(3);
  if (typeof v === 'object' && v !== null) return JSON.stringify(v);
  return String(v);
}

function KV({ k, v, cls = '' }) {
  return (
    <div className="ind-kv">
      <span className="k">{k}</span>
      <span className={`v ${cls}`}>{v}</span>
    </div>
  );
}
